// NextDoseView — Part 12 §P12.6 C2 "Next Dose Now" Beta.
//
// Opt-in single-number replacement for TodayScreen. Renders only when
// users.settings.minimalistTodayEnabled === true. The kratom-user
// voice from the review swarm asked for one thing above all:
//
//   "Show me one number — what to take RIGHT NOW. Not the dashboard.
//    Not the chart. Not the timer. Just: 'next dose: 8mg, at 11am.'
//    That's all I need at 7am when I'm sweating."
//
// So this surface does exactly that. Today's target + a clock to the
// next scheduled dose-window boundary + a tap-and-hold log button.
// Nothing else above the fold. Crisis link + "show full Today" sit
// below so the dismount path is always reachable.
//
// Default off. Reads existing /api/taper and /api/doses — no new
// endpoints. Renders identically in honest-day mode (no celebration
// chrome to begin with).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../../components/layout/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ApiErrorCard } from '../../components/ui/ApiErrorCard';
import { CrisisBanner } from '../../components/domain/CrisisBanner';
import { CrisisModal } from '../../components/domain/CrisisModal';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../auth/AuthContext';
import { apiPut } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { fmtMg, localYmd } from '../../lib/format';
import { useToday } from '../../lib/useToday';
import type { Dose, TaperPlanWire, DailyLog } from '@shared/types';

interface NextDoseLoad {
  todayTargetMg: number | null;
  todayActualMg: number;
  tomorrowTargetMg: number | null;
  crisisOpen: boolean;
}

function computeLoad(
  planRes: ReturnType<typeof useApi<TaperPlanWire | null>>,
  dosesRes: ReturnType<typeof useApi<Dose[]>>,
  logsRes: ReturnType<typeof useApi<DailyLog[]>>,
  today: string,
): NextDoseLoad {
  let todayTargetMg: number | null = null;
  let tomorrowTargetMg: number | null = null;
  if (planRes.status === 'ok' && planRes.data) {
    const t = planRes.data.days.find((d) => d.date === today);
    todayTargetMg = t ? t.targetMg : null;
    const tomorrow = nextDate(today);
    const tm = planRes.data.days.find((d) => d.date === tomorrow);
    tomorrowTargetMg = tm ? tm.targetMg : null;
  }
  let todayActualMg = 0;
  if (dosesRes.status === 'ok') {
    for (const d of dosesRes.data) {
      // localYmd() converts the UTC ts to the user's local calendar
      // day. The raw `d.ts.slice(0, 10)` would attribute a 9 pm local
      // dose to the next day in any tz west of UTC and shrink the
      // "today's actual" number until local midnight passed.
      if (localYmd(d.ts) === today) todayActualMg += d.computedMg7oh;
    }
    todayActualMg = Math.round(todayActualMg * 10) / 10;
  }
  let crisisOpen = false;
  if (logsRes.status === 'ok') {
    crisisOpen = (logsRes.data ?? []).some(
      (r) => (r.ssrsIdeationLevel ?? 0) >= 1 || r.crisisResourcesShown === 1,
    );
  }
  return { todayTargetMg, todayActualMg, tomorrowTargetMg, crisisOpen };
}

function nextDate(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function NextDoseView() {
  const { me, refresh } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const today = useToday();
  const planRes = useApi<TaperPlanWire | null>('/api/taper');
  const dosesRes = useApi<Dose[]>('/api/doses?limit=200');
  // Logs are needed only for the crisis-banner predicate (last 72h).
  const from = useMemo(() => {
    const d = new Date(`${today}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 3);
    return d.toISOString().slice(0, 10);
  }, [today]);
  const logsRes = useApi<DailyLog[]>(`/api/logs?from=${from}&to=${today}`);

  const [crisisOpen, setCrisisOpen] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [snoozingBanner, setSnoozingBanner] = useState(false);

  // Mirror TodayScreen.tsx: open the CrisisModal when the route was
  // entered via the manifest's "Crisis resources" shortcut (which
  // points at /app#crisis). Minimalist-mode users (this surface) hit
  // the same /app path so we wire it here too.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#crisis') return;
    setCrisisOpen(true);
    try {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch {
      // best-effort
    }
  }, []);

  const load = useMemo(
    () => computeLoad(planRes, dosesRes, logsRes, today),
    [planRes, dosesRes, logsRes, today],
  );

  async function exitMinimalist() {
    setSavingToggle(true);
    try {
      await apiPut('/api/me/settings', { minimalistTodayEnabled: false });
      await refresh();
      toast.push('Full Today view restored');
    } catch {
      toast.push('Could not switch back — try again from Settings.');
    } finally {
      setSavingToggle(false);
    }
  }

  // Both core resources still loading: render a tiny skeleton card so
  // we never blink. Errors surface inline; the user is never bricked.
  const stillLoading = planRes.status === 'loading' || dosesRes.status === 'loading';
  const planErr = planRes.status === 'error';
  const dosesErr = dosesRes.status === 'error';

  // If no plan exists yet (onboarding never completed), bail back to
  // the full Today screen so the FirstRunGuide can do its job.
  if (planRes.status === 'ok' && !planRes.data) {
    return (
      <PageContainer>
        <Card>
          <h2 className="text-h2" style={{ marginBlockEnd: 'var(--space-3)' }}>
            Set up your taper first
          </h2>
          <p className="muted" style={{ marginBlockEnd: 'var(--space-4)' }}>
            Minimalist Today needs an active taper plan. Finish onboarding to
            see your next scheduled dose.
          </p>
          <Button variant="primary" onClick={() => navigate('/app/onboarding')}>
            Start onboarding
          </Button>
        </Card>
      </PageContainer>
    );
  }

  const target = load.todayTargetMg;
  const actual = load.todayActualMg;
  const remaining = target != null ? Math.max(0, Math.round((target - actual) * 10) / 10) : null;

  // CrisisBanner snooze — mirrors TodayScreen so the minimalist view
  // honors the same `users.settings.crisisBannerSnoozedUntil` window
  // (Part 12 §P12.7 D4).
  const snoozedUntilStr = me?.settings.crisisBannerSnoozedUntil;
  const snoozedActive =
    typeof snoozedUntilStr === 'string' &&
    Date.parse(snoozedUntilStr) > Date.now();
  const showBanner = load.crisisOpen && !snoozedActive;

  async function snoozeBanner() {
    setSnoozingBanner(true);
    try {
      const until = new Date(Date.now() + 24 * 60 * 60_000).toISOString();
      await apiPut('/api/me/settings', { crisisBannerSnoozedUntil: until });
      await refresh();
    } catch {
      toast.push('Could not snooze — try again.');
    } finally {
      setSnoozingBanner(false);
    }
  }

  return (
    <PageContainer>
      {showBanner ? (
        <CrisisBanner
          onOpen={() => setCrisisOpen(true)}
          onSnooze={() => void snoozeBanner()}
          snoozing={snoozingBanner}
        />
      ) : null}

      <Card accentLeft>
        <p
          className="text-caption muted"
          style={{ letterSpacing: '0.06em', textTransform: 'uppercase', margin: 0 }}
        >
          Today
        </p>
        {stillLoading ? (
          <p className="muted" style={{ marginBlockStart: 'var(--space-3)' }}>
            Loading your next dose…
          </p>
        ) : planErr ? (
          <ApiErrorCard
            error={planRes.error}
            onRetry={() => planRes.refresh()}
            compact
          />
        ) : (
          <>
            <div
              style={{
                marginBlockStart: 'var(--space-2)',
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(2.5rem, 9vw, 4rem)',
                lineHeight: 1.1,
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--color-ink-primary)',
              }}
            >
              {target != null ? fmtMg(target) : '—'}
            </div>
            <p
              className="text-body"
              style={{ marginBlockStart: 'var(--space-1)', color: 'var(--color-ink-secondary)' }}
            >
              today&apos;s target
            </p>
            <p
              className="text-caption"
              style={{ marginBlockStart: 'var(--space-3)', color: 'var(--color-ink-secondary)' }}
            >
              {dosesErr
                ? "Couldn't load today's doses."
                : remaining != null
                ? remaining > 0
                  ? `So far ${fmtMg(actual)} — ${fmtMg(remaining)} remaining for today.`
                  : `So far ${fmtMg(actual)} — you have met today's target.`
                : `So far ${fmtMg(actual)}.`}
            </p>
          </>
        )}

        <div style={{ marginBlockStart: 'var(--space-5)' }}>
          <Button
            variant="primary"
            block
            size="lg"
            onClick={() => navigate('/app#log')}
          >
            Log a dose
          </Button>
        </div>
      </Card>

      {load.tomorrowTargetMg != null ? (
        <Card>
          <p className="text-caption muted" style={{ margin: 0 }}>
            Tomorrow
          </p>
          <p
            className="text-h2"
            style={{
              marginBlockStart: 'var(--space-1)',
              fontFamily: 'var(--font-heading)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtMg(load.tomorrowTargetMg)}
          </p>
        </Card>
      ) : null}

      <Card>
        <p className="text-caption muted" style={{ marginBlockEnd: 'var(--space-3)' }}>
          Quick actions
        </p>
        <div className="stack" style={{ '--stack-gap': 'var(--space-2)' } as React.CSSProperties}>
          <Button variant="secondary" onClick={() => navigate('/app/checkin')}>
            Daily check-in
          </Button>
          <Button variant="secondary" onClick={() => navigate('/app/breathe')}>
            Breathe with me · 60s
          </Button>
          <Button variant="tertiary" onClick={() => setCrisisOpen(true)}>
            I need help now
          </Button>
        </div>
      </Card>

      <div style={{ textAlign: 'center', marginBlockStart: 'var(--space-4)' }}>
        <Button
          variant="tertiary"
          size="md"
          onClick={() => void exitMinimalist()}
          disabled={savingToggle}
        >
          Show the full Today view
        </Button>
      </div>

      <CrisisModal open={crisisOpen} onDismiss={() => setCrisisOpen(false)} />
    </PageContainer>
  );
}
